/**
 * This source code is quoted from rc-steps.
 * homepage: https://github.com/react-component/steps
 */
import classNames from 'classnames';
// import PropTypes from 'prop-types';
import React, { ReactNode, isValidElement, useCallback, forwardRef, useMemo } from 'react';
// import {Icon} from "../../index";
import Icon from '../../wui-icon/src';
import Progress from '../../wui-progress/src';
import {StepProps, StepStatus} from './iSteps'

function isString(str: string | ReactNode) {
    return typeof str === 'string';
}

const iconMap = {
    finish: <Icon type="uf-yiwancheng"/>,
    process: <Icon type="uf-jinhangzhong"/>,
    error: <Icon type="uf-jinhangzhong"/>,
    wait: <Icon type="uf-weikaishi"/>
}

const Step: React.FC<StepProps> = React.memo(forwardRef<HTMLDivElement, StepProps>((props, ref) => {
    const {
        className, clsPrefix, style, itemWidth,
        status = 'wait', icon,
        adjustMarginRight, stepNumber,
        description, title, tailContent,
        more, out, disabled, stepIndex,
        onStepClick, onClick, active, type,
        stepFieldId, subTitle, percent,
        progressDot, direction, iconPrefix, icons, size,
        ...others
    } = props;

    const handleClick = useCallback((...args: []) => {
        if (onClick) {
            onClick(...args);
        }
        onStepClick?.(stepIndex);
    }, [onClick, onStepClick, stepIndex]);

    const isValidNode = useCallback((node: React.ReactNode): string | undefined => {
        return isValidElement(node) ? undefined : typeof node === 'string' ? node : undefined;
    }, []);

    const renderIconNode = useCallback(() => {
        let iconNode;
        const iconClassName = classNames(`${clsPrefix}-icon`, `${iconPrefix}icon`, {
            [`${iconPrefix}icon-${icon}`]: icon && isString(icon),
            [`${iconPrefix}icon-check`]: !icon && status === 'finish' && (icons && !icons.finish),
            [`${iconPrefix}icon-close`]: !icon && status === 'error' && (icons && !icons.error),
        });
        const iconDot = <span className={`${clsPrefix}-icon-dot`}></span>;

        // `progressDot` enjoy the highest priority
        if (progressDot || type === 'dot') { // 点状步骤条
            if (typeof progressDot === 'function') {
                iconNode = (
                    <span className={`${clsPrefix}-icon`}>
                        {progressDot(iconDot, {index: (stepNumber as number) - 1, status, title, description})}
                    </span>
                );
            } else {
                iconNode = <span className={`${clsPrefix}-icon`}>{iconDot}</span>;
            }
        } else if (icon && !isString(icon)) { // 自定义icon样式
            iconNode = <span className={`${clsPrefix}-icon`}>{icon}</span>;
        } else if (type && type === 'arrow') { // 箭头步骤条
            let iconDom: ReactNode = '';
            if (status === 'finish') {
                iconDom = <Icon type="uf-correct-2"/>;
            } else if (status === 'error') {
                iconDom = <Icon type="uf-exc-t"/>
            }
            iconNode = <span className={`${clsPrefix}-icon`}>{iconDom}</span>;
        } else if (type && type === 'number') { // 数字步骤条
            iconNode = <span className={`${clsPrefix}-icon`}>{stepNumber}</span>;
        } else if (icons && icons.finish && status === 'finish') {
            iconNode = <span className={`${clsPrefix}-icon`}>{icons.finish}</span>;
        } else if (icons && icons.error && status === 'error') {
            iconNode = <span className={`${clsPrefix}-icon`}>{icons.error}</span>;
        } else {
            if (status === 'process' && percent !== undefined && type === 'default') {
                const width = size === 'default' ? 20 : 28;
                iconNode = <span className={iconClassName}>
                    <Progress
                        type="circle"
                        percent={percent}
                        width={width}
                        strokeWidth={8}
                        format={() => ''}
                    />
                    {iconMap[status as StepStatus]}
                </span>;
            } else {
                iconNode = <span className={iconClassName}>{iconMap[status as StepStatus]}</span>;
            }
        }

        return iconNode;
    }, [clsPrefix, progressDot, stepNumber, status, title, description, icon, iconPrefix, icons, type, percent, size]);

    // 优化1: 缓存计算标志
    const computedFlags = useMemo(() => ({
        isOnchange: onStepClick && !disabled,
        isProgress: status === 'process' && percent !== undefined && type === 'default'
    }), [onStepClick, disabled, status, percent, type]);

    const { isOnchange, isProgress } = computedFlags;

    // 优化2: 使用useMemo缓存className计算
    const classString = useMemo(() => classNames(
        `${clsPrefix}-item`,
        `${clsPrefix}-item-${status}`,
        className,
        {
            [`${clsPrefix}-item-custom`]: icon,
            [`${clsPrefix}-more-out`]: out,
            [`${clsPrefix}-disabled`]: disabled,
            [`${clsPrefix}-item-active`]: active,
            [`${clsPrefix}-item-hover`]: isOnchange
        },
    ), [clsPrefix, status, className, icon, out, disabled, active, isOnchange]);

    const stepItemStyle = useMemo(() => {
        let tempStyle = {...style};
        if (itemWidth) {
            tempStyle.width = itemWidth;
        }
        if (adjustMarginRight) {
            tempStyle.marginRight = adjustMarginRight;
        }
        return tempStyle;
    }, [style, itemWidth, adjustMarginRight])

    // 优化3: 缓存accessibilityProps对象
    const accessibilityProps = useMemo(() => {
        const accessProps: Pick<StepProps, 'onClick' | 'fieldid'> = {};
        if (isOnchange) {
            accessProps.onClick = handleClick;
        }
        if (props.fieldid || stepFieldId) {
            accessProps.fieldid = props.fieldid ?? `${stepFieldId}_steps_icon_${stepIndex}`;
        }
        return accessProps;
    }, [isOnchange, handleClick, props.fieldid, stepFieldId, stepIndex]);

    // 优化4: 缓存subTitleDom计算
    const subTitleDom = useMemo(() =>
        subTitle ? (<div className={`${clsPrefix}-item-subtitle`} title={isValidNode(subTitle)}>{subTitle}</div>) : '',
    [subTitle, clsPrefix, isValidNode]
    );

    const defaultContentCls = type === 'default' && direction === 'horizontal' && !progressDot && !more ? `${clsPrefix}-item-content-width-control` : ''; // 默认模式启用自动宽度计算

    if (type && type === 'arrow') {
        return (
            <div
                {...others}
                ref={ref}
                className={classString}
                style={stepItemStyle}
                data-text={isValidNode(title)}
                data-num={stepIndex}
                data-status={disabled}
                onClick={onClick}
                {...accessibilityProps}
            >
                <svg className={`${clsPrefix}-arrow-before`} viewBox="0 0 32 32" >
                    <polygon strokeLinejoin="round" points="0,0 32,0 32,32 0,32 10,16"></polygon>
                </svg>
                <div className={`${clsPrefix}-item-number`}>
                    {stepNumber}
                </div>
                <div className={`${clsPrefix}-item-content`}>
                    <div className={`${clsPrefix}-item-title`}>
                        <span title={isValidNode(title)}>{title}{subTitleDom}</span>
                        {renderIconNode()}
                    </div>
                    {description &&
                        <div className={`${clsPrefix}-item-description`} title={isValidNode(description)}>{description}</div>}
                </div>
                <svg className={`${clsPrefix}-arrow-after`} viewBox="0 0 32 32" >
                    <polygon strokeLinejoin="round" points="0,0 22,0 32,16 22,32 0,32"></polygon>
                </svg>
            </div>
        )
    }

    return (
        <div
            {...others}
            ref={ref}
            className={classString}
            style={stepItemStyle}
            data-text={isValidNode(title)}
            data-num={stepIndex}
            data-status={disabled}
            onClick={onClick}
            {...accessibilityProps}
        >
            <div className={`${clsPrefix}-item-tail`}>
                {tailContent}
            </div>
            <div className={`${clsPrefix}-item-icon ${isProgress ? clsPrefix + '-item-progress-icon' : ''}`}>
                {renderIconNode()}
            </div>
            <div className={`${clsPrefix}-item-content ${defaultContentCls}`}>
                {
                    defaultContentCls ?
                        (
                            <div className={`${clsPrefix}-item-title`} title={isValidNode(title)}>
                                <div className={`${clsPrefix}-item-title-content`}>{title}{subTitleDom}</div>
                            </div>
                        ) :
                        (
                            <div className={`${clsPrefix}-item-title`} title={isValidNode(title)}>
                                {title}{subTitleDom}
                            </div>
                        )
                }
                {description && <div className={`${clsPrefix}-item-description`}>{description}</div>}
            </div>
            {more && type === 'default' && (<div className={`${clsPrefix}-item-line`}></div>)}
        </div>
    );
}));

// 添加displayName以便调试
Step.displayName = 'Step';

export default Step;